import React from 'react'
import { convertTime, formatTimeToWords, timeToSeconds } from '../../../../utils/timeUtils'
import { TimeCell } from './TimeCell';
import { HiOutlineArrowPath } from 'react-icons/hi2';

const KitchenReportTable = ({
    loading,
    error,
    orders,
    handleReopenOrder,
    isKitchen = true
}) => {
    if (loading || error) return;

    return (
        <div className="flex flex-col gap-6">
            {orders.map((element, index) => {
                const isValidTime = /^\d{2}:\d{2}:\d{2}$/.test(element.TotalTime);

                return (
                    <div
                        key={index}
                        className="bg-white rounded-lg border border-[#E0DDD8] overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-[#F8F7F3] border-b border-[#E0DDD8] px-4 py-2.5">
                            <div className="flex flex-wrap gap-3 justify-between">
                                <div className="flex gap-2 items-center">
                                    <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                        Table :
                                    </div>
                                    <div className="text-sm font-semibold text-[#2C2C2A]">
                                        {element.TableNum === "Take-Away"
                                            ? "Take-Away"
                                            : `Table ${element.TableNum}`}
                                    </div>
                                </div>

                                <div className="flex gap-2 items-center">
                                    <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                        Employee :
                                    </div>
                                    <div className="text-sm font-semibold text-[#2C2C2A]">
                                        {element.Employee}
                                    </div>
                                </div>

                                <div className="flex gap-2 items-center">
                                    <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                        KOT ID :
                                    </div>
                                    <div className="text-sm font-semibold text-[#2C2C2A]">
                                        {element.KOTID}
                                    </div>
                                </div>

                                {isKitchen && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReopenOrder(element.idtblorderTracker);
                                        }}
                                        disabled={!isValidTime}
                                        title={!isValidTime ? "Cannot reopen this order" : "Reopen Order"}
                                        className={`
                                        p-2 rounded-md
                                        transition-all duration-150
                                        ${isValidTime
                                                ? "text-sky-600 hover:bg-sky-100 hover:text-sky-700 active:scale-95"
                                                : "text-gray-400 cursor-not-allowed opacity-50"
                                            }
                                    `}
                                    >
                                        <HiOutlineArrowPath size={18} className="stroke-2" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto tiny-scrollbar">
                            <table className="w-full text-sm text-center">
                                <thead>
                                    <tr className="border-b border-[#F0EDE8] bg-white">
                                        {[
                                            "#",
                                            "Qty",
                                            "Item",
                                            "Ordered At",
                                            "Completed At",
                                            "Total Time",
                                            "Status",
                                            "Avg Prep Time",
                                            "Prep Difference",
                                        ].map((header) => (
                                            <th
                                                key={header}
                                                className="px-2 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#8A8780] whitespace-nowrap"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {element.items.map((item, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b border-[#F8F7F3] hover:bg-[#FAFAF8] transition-colors duration-100"
                                        >
                                            {/* S.No */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                {idx + 1}
                                            </td>

                                            {/* Qty */}
                                            <td className="px-2.5 py-2 font-semibold text-[#4B4B46]">
                                                {item.Quantity}
                                            </td>

                                            {/* Item */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#2C2C2A]">
                                                {item.ItemName}
                                            </td>

                                            {/* Ordered At */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                {convertTime(item.orderedAt)}
                                            </td>

                                            {/* Completed At */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                <TimeCell value={item.completedAt} convert />
                                            </td>

                                            {/* Total Time */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                <TimeCell value={item.TotalTime} />
                                            </td>

                                            {/* Status */}
                                            <td className="px-2.5 py-2">
                                                {item.voidTotalTime ? (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
                                                        style={{
                                                            background: "#FEF2F2",
                                                            borderColor: "#FECACA",
                                                            color: "#DC2626",
                                                        }}
                                                    >
                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
                                                        Void
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
                                                        style={{
                                                            background: "#ECFDF5",
                                                            borderColor: "#A7F3D0",
                                                            color: "#059669",
                                                        }}
                                                    >
                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#059669]" />
                                                        Completed
                                                    </span>
                                                )}
                                            </td>

                                            {/* Avg Prep Time */}
                                            <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                {item.prepTimeDifference ? (
                                                    formatTimeToWords(item.AvgPrepTime)
                                                ) : (
                                                    <span className="text-[#C7C3BD]">—</span>
                                                )}
                                            </td>

                                            {/* Prep Difference */}
                                            <td className="px-2.5 py-2 whitespace-nowrap">
                                                {item.prepTimeDifference ? (
                                                    <span
                                                        className={`
                                                        px-2 py-1
                                                        rounded-full
                                                        text-xs font-semibold
                                                        border
                                                        ${timeToSeconds(item.TotalTime) > timeToSeconds(item.AvgPrepTime)
                                                                ? "bg-red-50 text-red-600 border-red-200"
                                                                : "bg-green-50 text-green-600 border-green-200"
                                                            }
                                                    `}
                                                    >
                                                        {formatTimeToWords(item.prepTimeDifference)}
                                                    </span>
                                                ) : (
                                                    <span className="text-[#C7C3BD]">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default KitchenReportTable